import { computed, reactive, ref, watch } from 'vue'
import { Order, OrderRequest, OrderTicket, OrderTicketStatus, Shift, TableSessionService, appStorage, AppStorageKey, I18n } from 'app-core'
import { useOrderRequests, type ConfirmForeignTable } from './useOrderRequests'
import { useOrderTickets } from './useOrderTickets'
import type {
    OrdersHubItem,
    OrdersHubItemQrPending,
    OrdersHubItemOrderTicket,
    OrdersHubTab,
    OrdersHubSource,
} from '@/types/ordersHub'
import { useAppStore } from '@/stores/useAppStore'

// ── Adapter functions (pure) ─────────────────────────────────────────────────

function adaptQrPending(request: OrderRequest, key: string, servedBy: string | null): OrdersHubItemQrPending {
    return {
        kind: 'qr_pending',
        key,
        source: 'qr',
        tab: 'needs_action',
        displayId: request.getTableName(),
        createdAt: request.created_at || new Date().toISOString(),
        raw: request,
        servedBy,
        isRead: false, // computed later via tab timestamps
    }
}

function adaptOrderTicket(ticket: OrderTicket, key: string): OrdersHubItemOrderTicket {
    return {
        kind: 'order_ticket',
        key,
        source: ticket.source || 'other',
        tab: mapOrderTicketToTab(ticket),
        // Unified delivery identifier (allocated at submitAsTicket for manual,
        // at fireToKitchen for API). Before the fire there is no callout yet,
        // so fall back to the platform's own order code — a waiter can match
        // that against the platform; the literal 'TICKET' told them nothing.
        displayId: `#${ticket.callout_code || ticket.external_code || 'TICKET'}`,
        createdAt: ticket.updated_at || ticket.created_at,
        raw: ticket,
        isRead: false, // computed later via tab timestamps
    }
}

function isStaleCompleted(ticket: OrderTicket, now: number): boolean {
    const status = ticket.status
    if (
        status !== OrderTicketStatus.pickedUp &&
        status !== OrderTicketStatus.completed &&
        status !== OrderTicketStatus.cancelled
    ) return false

    // A delivery the platform completed before staff issued its receipt is
    // unfinished business, not history: the goods are gone and no fiscal
    // receipt exists. Evicting it hid the only affordance that can still
    // produce one, so it stays until it is issued (or cancelled, which is
    // never issuable and therefore still evicts normally).
    if (
        status !== OrderTicketStatus.cancelled
        && !ticket.order_hash
        && !ticket.order_id
    ) return false

    const touched = new Date(ticket.updated_at || ticket.created_at).getTime() || 0

    // Terminal-state tickets live for the WHOLE SHIFT: the Completed tab
    // keeps the shift's history, and — critically — cancelled tickets must
    // keep holding their callout reservation, which computeCalloutCode
    // counts against the same shift-open boundary (evicting them early
    // would shift the sequence down and reuse an already-printed code).
    // They evict once the shift they ended in is over: when a newer shift
    // has opened since they were last touched, or no shift is open at all.
    const shiftOpenedAt = Shift.current()?.opened_at
    if (shiftOpenedAt) {
        return touched < new Date(shiftOpenedAt).getTime()
    }

    // No open shift on this terminal (shift just closed, or a terminal type
    // that never opens one): fall back to the age window.
    return now - touched > COMPLETED_EVICTION_MS
}

function mapOrderTicketToTab(ticket: OrderTicket): OrdersHubTab {
    // A ticket nobody has released to the kitchen yet belongs under Needs
    // action even once the platform has advanced its status on its own —
    // otherwise (staff-confirmation mode) it sits among the orders that ARE
    // cooking, and the queue staff are asked to work looks empty.
    if (ticket.needsStaffKitchenRelease()) return 'needs_action'

    return mapOrderTicketStatusToTab(ticket.status)
}

function mapOrderTicketStatusToTab(status: OrderTicketStatus): OrdersHubTab {
    switch (status) {
        case OrderTicketStatus.accepted:
        case OrderTicketStatus.needsMapping:
            return 'needs_action'
        case OrderTicketStatus.preparing:
            return 'in_progress'
        case OrderTicketStatus.ready:
            return 'ready'
        case OrderTicketStatus.pickedUp:
        case OrderTicketStatus.completed:
            return 'completed'
        case OrderTicketStatus.cancelled:
            return 'completed'
        default:
            return 'needs_action'
    }
}

// ── Constants ────────────────────────────────────────────────────────────────

/**
 * Fallback eviction age for terminal-state tickets, used only when no shift
 * is open on this terminal. While a shift is open, terminal-state tickets
 * are kept for the whole shift (history + callout reservations) and evict
 * at the shift boundary instead — see isStaleCompleted.
 */
const COMPLETED_EVICTION_MS = 30 * 60 * 1000 // 30 minutes

// ── Module-level singleton state ─────────────────────────────────────────────

type TabViewedAt = Record<OrdersHubTab, number>

const tabViewedAt = reactive<TabViewedAt>({
    needs_action: Date.now(),
    in_progress: Date.now(),
    ready: Date.now(),
    completed: Date.now(),
})
let tabViewedAtLoaded = false

async function loadTabViewedAt() {
    if (tabViewedAtLoaded) return
    tabViewedAtLoaded = true
    const stored = await appStorage.local().getItem<TabViewedAt>(AppStorageKey.ordersHubTabViewedAt)
    if (stored) {
        tabViewedAt.needs_action = stored.needs_action ?? Date.now()
        tabViewedAt.in_progress = stored.in_progress ?? Date.now()
        tabViewedAt.ready = stored.ready ?? Date.now()
        tabViewedAt.completed = stored.completed ?? Date.now()
    }
}

function persistTabViewedAt() {
    appStorage.local().setItem(AppStorageKey.ordersHubTabViewedAt, { ...tabViewedAt })
}

const activeTab = ref<OrdersHubTab>('needs_action')
const sourceFilter = ref<OrdersHubSource | 'all'>('all')
const searchQuery = ref('')
const selectedKey = ref<string | null>(null)

// ── Composable ───────────────────────────────────────────────────────────────

export function useOrdersHub() {
    const store = useAppStore()

    const qrEnabled = computed(() => TableSessionService.isQrOrderingEnabled())

    /**
     * True when the paid delivery-API module is on — controls visibility
     * of API-only UI (product mapping modal, reject-to-platform button,
     * rider ETA card).
     */
    const deliveryApiEnabled = computed(() => !!store.modules?.delivery)

    /**
     * Locale-configured manual delivery platforms (lowercased source
     * tokens like 'glovo' / 'wolt'). Empty array on locales where manual
     * delivery is not offered.
     */
    const configuredDeliveryPlatforms = computed<string[]>(() => {
        const list = I18n.c<string[]>('order.deliveryPlatforms')
        return Array.isArray(list) ? list : []
    })

    /**
     * True when the Orders Hub has anything meaningful to show — i.e.,
     * the API module is on OR the locale has at least one configured
     * manual delivery platform.
     */
    const orderTicketsVisible = computed(() =>
        deliveryApiEnabled.value || configuredDeliveryPlatforms.value.length > 0
    )

    // Server-gated: the hub (and its poller) only exist for tenants that can
    // receive orders into it — delivery module on, QR ordering enabled, or at
    // least one order ticket on record. `ordersHubActive` already encodes
    // qrEnabled / delivery; keeping them in the OR is harmless but redundant.
    // The flag flips true the instant a first ticket arrives (see App.vue /
    // useDeliveryOrder), so the hub appears without a reload.
    const isVisible = computed(() => store.ordersHubActive)

    // Call composables unconditionally — they are lightweight computed wrappers
    // over Pinia ORM queries with no cost when underlying stores are empty.
    const orderRequests = useOrderRequests()
    const orderTickets = useOrderTickets()

    // Load persisted tab timestamps on first use
    loadTabViewedAt()

    // ── Unified item list ──
    // tabViewedAt is reactive — Vue tracks property access for each tab

    const allItems = computed<OrdersHubItem[]>(() => {
        const items: OrdersHubItem[] = []

        // QR pending adapter (OrderRequests awaiting approval)
        // Once approved, QR orders become regular table Orders visible in the hospitality view —
        // they don't belong in the Orders Hub anymore.
        if (qrEnabled.value) {
            // One scan of the open rounds for the whole list — this computed
            // feeds the always-mounted sidebar badge and re-runs on every
            // order-store write.
            const onTables = Order.onTables()
            for (const req of orderRequests.pendingOrderRequests.value) {
                const key = `qr-req:${req.id}`
                const item = adaptQrPending(req, key, orderRequests.otherTableWaiter(req, onTables)?.name ?? null)
                item.isRead = new Date(item.createdAt).getTime() <= tabViewedAt[item.tab]
                items.push(item)
            }
        }

        // Order ticket adapter — skip completed/cancelled tickets older than eviction threshold
        if (orderTicketsVisible.value) {
            const now = Date.now()
            for (const ticket of orderTickets.orderTickets.value) {
                if (isStaleCompleted(ticket, now)) continue
                const key = `del:${ticket.hash}`
                const item = adaptOrderTicket(ticket, key)
                item.isRead = new Date(item.createdAt).getTime() <= tabViewedAt[item.tab]
                items.push(item)
            }
        }

        return items
    })

    // ── Tab-filtered list ──

    const filteredItems = computed(() => {
        let items = allItems.value.filter(item => item.tab === activeTab.value)

        // Source filter
        if (sourceFilter.value !== 'all') {
            items = items.filter(item => item.source === sourceFilter.value)
        }

        // Search. displayId switches to the callout code the moment a ticket
        // fires, but the courier at the counter only knows the PLATFORM's code
        // — so the external code must stay findable for the whole lifetime.
        if (searchQuery.value.trim()) {
            const q = searchQuery.value.trim().toLowerCase()
            items = items.filter(item =>
                item.displayId.toLowerCase().includes(q) ||
                item.key.toLowerCase().includes(q) ||
                (item.kind === 'order_ticket' && (item.raw.external_code || '').toLowerCase().includes(q))
            )
        }

        // Sort: newest first, with issued tickets pushed to the bottom pile
        items.sort((a, b) => {
            const aIssued = a.kind === 'order_ticket' && !!a.raw.order_hash ? 1 : 0
            const bIssued = b.kind === 'order_ticket' && !!b.raw.order_hash ? 1 : 0
            if (aIssued !== bIssued) return aIssued - bIssued
            const dateA = new Date(a.createdAt).getTime() || 0
            const dateB = new Date(b.createdAt).getTime() || 0
            return dateB - dateA
        })

        return items
    })

    // ── Tab counts ──

    const tabCounts = computed(() => ({
        needs_action: allItems.value.filter(i => i.tab === 'needs_action').length,
        in_progress: allItems.value.filter(i => i.tab === 'in_progress').length,
        ready: allItems.value.filter(i => i.tab === 'ready').length,
        completed: allItems.value.filter(i => i.tab === 'completed').length,
    }))

    /** Per-tab unread counts (for tab badge styling) */
    const tabUnreadCounts = computed(() => ({
        needs_action: allItems.value.filter(i => i.tab === 'needs_action' && !i.isRead).length,
        in_progress: allItems.value.filter(i => i.tab === 'in_progress' && !i.isRead).length,
        ready: allItems.value.filter(i => i.tab === 'ready' && !i.isRead).length,
        completed: 0,
    }))

    /** Badge count for sidebar = unread items across non-completed tabs */
    const badgeCount = computed(() => {
        const { needs_action, in_progress, ready } = tabUnreadCounts.value
        const count = needs_action + in_progress + ready
        return count || null
    })

    /**
     * True when any non-completed lane (needs_action / in_progress / ready)
     * holds at least one item — drives the warning-colored sidebar bell icon.
     * Unlike badgeCount this counts every active item, read or not.
     */
    const hasActiveOrders = computed(() => {
        const { needs_action, in_progress, ready } = tabCounts.value
        return needs_action + in_progress + ready > 0
    })

    // ── Unread tracking ──

    function markTabViewed(tab?: OrdersHubTab) {
        const t = tab || activeTab.value
        tabViewedAt[t] = Date.now()
        persistTabViewedAt()
    }

    // ── Selection ──

    const selectedItem = computed(() => {
        if (!selectedKey.value) return null
        return allItems.value.find(i => i.key === selectedKey.value) || null
    })

    function selectItem(key: string) {
        selectedKey.value = key
    }

    // Clear selection when the selected item leaves the current filtered list
    // (moved to another tab via status change, removed, evicted, etc.)
    watch(filteredItems, (items) => {
        if (!selectedKey.value) return
        if (!items.some(i => i.key === selectedKey.value)) {
            selectedKey.value = null
        }
    })

    // ── Available sources (for filter dropdown) ──

    const availableSources = computed<OrdersHubSource[]>(() => {
        const sources: OrdersHubSource[] = []
        if (qrEnabled.value) sources.push('qr')
        if (orderTicketsVisible.value) {
            // Derive active sources from actual tickets
            const platforms = new Set(
                orderTickets.orderTickets.value.map(t => t.source)
            )
            for (const p of platforms) {
                if (p) sources.push(p)
            }
            // If no tickets yet but hub is visible, fall back to the
            // locale-configured platforms so the filter shows what the
            // POS is set up to support manual entry for.
            if (!platforms.size) {
                for (const source of configuredDeliveryPlatforms.value) {
                    if (source && !sources.includes(source)) sources.push(source)
                }
            }
        }
        return sources
    })

    /** If only one source, hide the filter entirely */
    const showSourceFilter = computed(() => availableSources.value.length > 1)

    // ── Actions (delegate to underlying composables) ──

    async function approveQrOrder(item: OrdersHubItemQrPending, confirmForeignTable: ConfirmForeignTable) {
        return orderRequests.approve(item.raw, confirmForeignTable)
    }

    async function rejectQrOrder(item: OrdersHubItemQrPending, reason?: string) {
        return orderRequests.reject(item.raw, reason)
    }

    return {
        // Feature flags
        isVisible,
        qrEnabled,
        orderTicketsVisible,
        deliveryApiEnabled,

        // State
        activeTab,
        sourceFilter,
        searchQuery,

        // Data
        filteredItems,
        tabCounts,
        tabUnreadCounts,
        badgeCount,
        hasActiveOrders,

        // Sources
        availableSources,
        showSourceFilter,

        // Selection
        selectedKey,
        selectedItem,
        selectItem,

        // Unread
        markTabViewed,

        // QR actions
        approveQrOrder,
        rejectQrOrder,
    }
}
